import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Phone, Mail, Link, Copy, Check, Loader2, ArrowRight, Shield, ChevronDown, Users, X } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { trackInviteSent, trackInvitePageOpened, trackInviteMethodSelected, trackInviteSkipped } from '@/utils/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Country codes
const COUNTRY_CODES = [
  { code: '+1', country: 'US', name: 'United States', flag: '🇺🇸' },
  { code: '+91', country: 'IN', name: 'India', flag: '🇮🇳' },
  { code: '+44', country: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: '+61', country: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: '+971', country: 'AE', name: 'UAE', flag: '🇦🇪' },
  { code: '+65', country: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: '+49', country: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'FR', name: 'France', flag: '🇫🇷' },
  { code: '+81', country: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: '+86', country: 'CN', name: 'China', flag: '🇨🇳' },
  { code: '+55', country: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: '+52', country: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: '+82', country: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: '+39', country: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: '+31', country: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: '+63', country: 'PH', name: 'Philippines', flag: '🇵🇭' },
  { code: '+92', country: 'PK', name: 'Pakistan', flag: '🇵🇰' },
  { code: '+880', country: 'BD', name: 'Bangladesh', flag: '🇧🇩' },
  { code: '+234', country: 'NG', name: 'Nigeria', flag: '🇳🇬' },
  { code: '+27', country: 'ZA', name: 'South Africa', flag: '🇿🇦' },
  { code: '+62', country: 'ID', name: 'Indonesia', flag: '🇮🇩' },
];

export default function InviteFriend() {
  const [user, setUser] = useState(null);
  const [method, setMethod] = useState('sms'); // 'sms', 'email', 'link'
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  
  // SMS fields
  const [phoneDigits, setPhoneDigits] = useState('');
  const [selectedCountryCode, setSelectedCountryCode] = useState('+91');
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  
  // Email fields
  const [toEmail, setToEmail] = useState('');
  
  // Common fields
  const [senderName, setSenderName] = useState('');
  const [message, setMessage] = useState('');
  
  const navigate = useNavigate();

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        
        // Generate invite link
        generateInviteLink();
      } catch (error) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);

  // Auto-detect country
  useEffect(() => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const countryMap = {
      'Asia/Kolkata': '+91',
      'Asia/Calcutta': '+91',
      'America/New_York': '+1',
      'America/Los_Angeles': '+1',
      'Europe/London': '+44',
      'Asia/Dubai': '+971',
      'Asia/Singapore': '+65',
      'Australia/Sydney': '+61',
    };
    if (countryMap[timezone]) {
      setSelectedCountryCode(countryMap[timezone]);
    }
  }, []);

  const generateInviteLink = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/invite/create`,
        { source_button: 'onboarding_link' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Use short URL if available
      if (response.data.short_url) {
        setInviteLink(response.data.short_url);
      } else if (response.data.invite_url) {
        setInviteLink(response.data.invite_url);
      }
    } catch (error) {
      console.error('Failed to generate invite link');
    }
  };

  const copyLink = async () => {
    if (!inviteLink) {
      await generateInviteLink();
    }
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(inviteLink);
        setCopied(true);
        // Track invite sent event in Meta Pixel
        trackInviteSent('link', 'onboarding_link');
        toast.success('Invite link copied!');
        setTimeout(() => setCopied(false), 3000);
        return;
      }
      
      // Fallback for Safari/iOS
      const textArea = document.createElement('textarea');
      textArea.value = inviteLink;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopied(true);
        // Track invite sent event in Meta Pixel
        trackInviteSent('link', 'onboarding_link');
        toast.success('Invite link copied!');
        setTimeout(() => setCopied(false), 3000);
      } else {
        toast.error('Failed to copy. Please copy manually.');
      }
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const sendInvite = async () => {
    if (method === 'sms') {
      const fullPhone = `${selectedCountryCode}${phoneDigits}`;
      if (!phoneDigits || phoneDigits.length < 6) {
        toast.error('Please enter a valid phone number');
        return;
      }
      if (!message.trim()) {
        toast.error('Please enter a message');
        return;
      }
      
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        await axios.post(
          `${API}/invite/send`,
          {
            channel: 'sms',
            to_phone: fullPhone,
            message: message,
            sender_name: senderName || 'Someone',
            source_button: 'onboarding_sms'
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Track invite sent event in Meta Pixel
        trackInviteSent('sms', 'onboarding_sms');
        toast.success('Invite sent via SMS!');
        navigate('/dashboard');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to send invite');
      } finally {
        setLoading(false);
      }
    } else if (method === 'email') {
      if (!toEmail || !toEmail.includes('@')) {
        toast.error('Please enter a valid email');
        return;
      }
      if (!message.trim()) {
        toast.error('Please enter a message');
        return;
      }
      
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        await axios.post(
          `${API}/invite/send`,
          {
            channel: 'email',
            to_email: toEmail,
            message: message,
            sender_name: senderName || 'Someone',
            source_button: 'onboarding_email'
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Track invite sent event in Meta Pixel
        trackInviteSent('email', 'onboarding_email');
        toast.success('Invite sent via Email!');
        navigate('/dashboard');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to send invite');
      } finally {
        setLoading(false);
      }
    }
  };

  const skipForNow = () => {
    navigate('/dashboard');
  };

  const selectedCountry = COUNTRY_CODES.find(c => c.code === selectedCountryCode) || COUNTRY_CODES[1];

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #0A0F2C 0%, #1a1f3c 50%, #0A0F2C 100%)' }}
    >
      <Card 
        className="w-full max-w-md border-0"
        style={{ background: 'rgba(10, 15, 44, 0.95)', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}
      >
        <CardHeader className="text-center pb-2 relative">
          {/* Close button */}
          <button
            onClick={skipForNow}
            className="absolute top-4 right-4 p-2 rounded-full transition-colors hover:bg-white/10"
            style={{ color: 'rgba(229, 231, 235, 0.5)' }}
            data-testid="close-btn"
          >
            <X className="h-5 w-5" />
          </button>
          
          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-1 rounded-full bg-purple-500" />
            <div className="w-8 h-1 rounded-full bg-purple-500" />
            <div className="w-8 h-1 rounded-full bg-purple-500" />
            <div className="w-8 h-1 rounded-full" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }} />
          </div>
          
          <div className="w-14 h-14 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.2) 0%, rgba(34, 211, 238, 0.2) 100%)' }}>
            <Users className="h-7 w-7" style={{ color: '#7C3AED' }} />
          </div>
          
          <CardTitle className="text-xl font-bold" style={{ color: '#E5E7EB' }}>
            Invite a Friend
          </CardTitle>
          <CardDescription style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
            Send an anonymous invite via SMS, Email, or copy link
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-5">
          {/* Method tabs */}
          <div className="flex rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <button
              onClick={() => setMethod('sms')}
              className={`flex-1 py-3 px-3 flex items-center justify-center gap-2 text-sm font-medium transition-all ${
                method === 'sms' ? 'text-white' : 'text-gray-400'
              }`}
              style={method === 'sms' ? { background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)' } : {}}
              data-testid="sms-tab"
            >
              <Phone className="h-4 w-4" />
              SMS
            </button>
            <button
              onClick={() => setMethod('email')}
              className={`flex-1 py-3 px-3 flex items-center justify-center gap-2 text-sm font-medium transition-all ${
                method === 'email' ? 'text-white' : 'text-gray-400'
              }`}
              style={method === 'email' ? { background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)' } : {}}
              data-testid="email-tab"
            >
              <Mail className="h-4 w-4" />
              Email
            </button>
            <button
              onClick={() => setMethod('link')}
              className={`flex-1 py-3 px-3 flex items-center justify-center gap-2 text-sm font-medium transition-all ${
                method === 'link' ? 'text-white' : 'text-gray-400'
              }`}
              style={method === 'link' ? { background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)' } : {}}
              data-testid="link-tab"
            >
              <Link className="h-4 w-4" />
              Copy Link
            </button>
          </div>

          {/* SMS Form */}
          {method === 'sms' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
                  Recipient Phone *
                </label>
                <div className="relative">
                  <Popover open={showCountryPicker} onOpenChange={setShowCountryPicker}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1 px-2 py-1 rounded-md transition-all hover:bg-white/10 z-10"
                        style={{ color: '#E5E7EB' }}
                      >
                        <span className="text-lg">{selectedCountry.flag}</span>
                        <span className="text-sm font-medium">{selectedCountryCode}</span>
                        <ChevronDown className="h-3 w-3 opacity-50" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent 
                      className="w-64 p-2 max-h-72 overflow-y-auto"
                      style={{ background: 'rgba(10,15,44,0.98)', border: '1px solid rgba(255,255,255,0.1)' }}
                    >
                      <div className="space-y-1">
                        {COUNTRY_CODES.map((country) => (
                          <button
                            key={country.code}
                            onClick={() => {
                              setSelectedCountryCode(country.code);
                              setShowCountryPicker(false);
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${selectedCountryCode === country.code ? 'bg-purple-500/20' : 'hover:bg-white/10'}`}
                          >
                            <span className="text-xl">{country.flag}</span>
                            <span className="text-sm flex-1 text-left" style={{ color: '#E5E7EB' }}>{country.name}</span>
                            <span className="text-sm font-mono" style={{ color: 'rgba(229,231,235,0.6)' }}>{country.code}</span>
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  
                  <Input
                    type="tel"
                    placeholder="Phone number"
                    value={phoneDigits}
                    onChange={(e) => setPhoneDigits(e.target.value.replace(/\D/g, ''))}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12 pl-28"
                    style={{ fontSize: '16px' }}
                    data-testid="phone-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
                  Your Name (how they'll know you)
                </label>
                <Input
                  type="text"
                  placeholder="e.g., A friend, Your secret admirer..."
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12"
                  style={{ fontSize: '16px' }}
                  data-testid="sender-name-input"
                />
                <p className="text-xs mt-1" style={{ color: 'rgba(229, 231, 235, 0.4)' }}>
                  Leave blank to send as "Someone"
                </p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
                  Message *
                </label>
                <Textarea
                  placeholder="Type your invite message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 min-h-[80px] resize-none"
                  style={{ fontSize: '16px' }}
                  data-testid="message-input"
                />
              </div>

              <div className="flex items-start gap-2 p-3 rounded-lg" style={{ background: 'rgba(124, 58, 237, 0.1)' }}>
                <Shield className="h-4 w-4 mt-0.5 shrink-0" style={{ color: '#7C3AED' }} />
                <p className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
                  Your phone number stays hidden. SMS sent from our system.
                </p>
              </div>

              <Button
                onClick={sendInvite}
                disabled={loading}
                className="w-full h-12 text-white font-semibold"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                data-testid="send-sms-btn"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                  <>Send Invite via SMS <ArrowRight className="h-4 w-4 ml-2" /></>
                )}
              </Button>
            </div>
          )}

          {/* Email Form */}
          {method === 'email' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
                  Recipient Email *
                </label>
                <Input
                  type="email"
                  placeholder="friend@example.com"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12"
                  style={{ fontSize: '16px' }}
                  data-testid="email-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
                  Your Name (how they'll know you)
                </label>
                <Input
                  type="text"
                  placeholder="e.g., A friend, Your secret admirer..."
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12"
                  style={{ fontSize: '16px' }}
                  data-testid="sender-name-email-input"
                />
                <p className="text-xs mt-1" style={{ color: 'rgba(229, 231, 235, 0.4)' }}>
                  Leave blank to send as "Someone"
                </p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
                  Message *
                </label>
                <Textarea
                  placeholder="Type your invite message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 min-h-[80px] resize-none"
                  style={{ fontSize: '16px' }}
                  data-testid="message-email-input"
                />
              </div>

              <div className="flex items-start gap-2 p-3 rounded-lg" style={{ background: 'rgba(124, 58, 237, 0.1)' }}>
                <Shield className="h-4 w-4 mt-0.5 shrink-0" style={{ color: '#7C3AED' }} />
                <p className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
                  Your email stays hidden. Email sent from our system.
                </p>
              </div>

              <Button
                onClick={sendInvite}
                disabled={loading}
                className="w-full h-12 text-white font-semibold"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                data-testid="send-email-btn"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                  <>Send Invite via Email <ArrowRight className="h-4 w-4 ml-2" /></>
                )}
              </Button>
            </div>
          )}

          {/* Copy Link */}
          {method === 'link' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
                  Your Private Invite Link
                </label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={inviteLink || 'Generating...'}
                    readOnly
                    className="bg-white/5 border-white/10 text-white h-12 flex-1"
                    style={{ fontSize: '14px' }}
                    data-testid="invite-link-input"
                  />
                  <Button
                    onClick={copyLink}
                    className="h-12 px-4"
                    style={{ background: copied ? '#22C55E' : 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                    data-testid="copy-link-btn"
                  >
                    {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                  </Button>
                </div>
              </div>

              <div className="text-center py-4">
                <p className="text-sm mb-4" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
                  Share this link with anyone you want to chat with privately.
                  They'll be able to join Privluxe and connect with you.
                </p>
                
                <div className="flex items-start gap-2 p-3 rounded-lg text-left" style={{ background: 'rgba(124, 58, 237, 0.1)' }}>
                  <Shield className="h-4 w-4 mt-0.5 shrink-0" style={{ color: '#7C3AED' }} />
                  <p className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
                    Link expires in 48 hours. Your identity remains private until you choose to reveal it.
                  </p>
                </div>
              </div>

              <Button
                onClick={copyLink}
                className="w-full h-12 text-white font-semibold"
                style={{ background: copied ? '#22C55E' : 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                data-testid="copy-link-main-btn"
              >
                {copied ? (
                  <><Check className="h-5 w-5 mr-2" /> Link Copied!</>
                ) : (
                  <><Copy className="h-5 w-5 mr-2" /> Copy Invite Link</>
                )}
              </Button>
            </div>
          )}

          {/* Skip button */}
          <button
            onClick={skipForNow}
            className="w-full text-sm py-2 transition-colors hover:text-white"
            style={{ color: 'rgba(229, 231, 235, 0.4)' }}
            data-testid="skip-btn"
          >
            Skip for now →
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
